'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function VoltageDropCalculator() {
  const t = useTranslations("tools.voltageDropCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Screen size detection
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  // Calculator state
  const [activeTab, setActiveTab] = useState<'nec' | 'estimated' | 'custom'>('nec');
  const [inputs, setInputs] = useState({
    // NEC tab
    necMaterial: 'copper',
    necWireSize: '3',
    necConduit: 'steel',
    necPf: 0.85,
    // Estimated tab
    estMaterial: 'copper',
    estWireSize: '0.4066',
    // Custom tab
    customResistance: 1.2,
    customResistanceUnit: 'okm',
    // Common inputs
    voltage: 120,
    phase: 'ac',
    conductors: 1,
    distance: 500,
    distanceUnit: 'feet',
    current: 1,
  });

  const [results, setResults] = useState<any>(null);

  // NEC resistance data
  const necResistanceData = {
    copper: {
      '14': { r: 3.07, x: 0.062 },
      '12': { r: 1.93, x: 0.059 },
      '10': { r: 1.21, x: 0.056 },
      '8': { r: 0.764, x: 0.052 },
      '6': { r: 0.491, x: 0.049 },
      '4': { r: 0.308, x: 0.046 },
      '3': { r: 0.245, x: 0.045 },
      '2': { r: 0.194, x: 0.043 },
      '1': { r: 0.154, x: 0.042 },
      '1/0': { r: 0.122, x: 0.041 },
      '2/0': { r: 0.0967, x: 0.040 },
      '3/0': { r: 0.0766, x: 0.039 },
      '4/0': { r: 0.0608, x: 0.038 },
      '250': { r: 0.0515, x: 0.037 },
      '300': { r: 0.0429, x: 0.036 },
      '350': { r: 0.0367, x: 0.036 },
      '400': { r: 0.0321, x: 0.035 },
      '500': { r: 0.0258, x: 0.034 },
      '600': { r: 0.0214, x: 0.034 }
    },
    aluminum: {
      '14': { r: 5.06, x: 0.065 },
      '12': { r: 3.18, x: 0.062 },
      '10': { r: 2.00, x: 0.059 },
      '8': { r: 1.26, x: 0.055 },
      '6': { r: 0.808, x: 0.051 },
      '4': { r: 0.508, x: 0.048 },
      '3': { r: 0.403, x: 0.047 },
      '2': { r: 0.319, x: 0.045 },
      '1': { r: 0.253, x: 0.044 },
      '1/0': { r: 0.201, x: 0.043 },
      '2/0': { r: 0.159, x: 0.042 },
      '3/0': { r: 0.126, x: 0.041 },
      '4/0': { r: 0.100, x: 0.040 },
      '250': { r: 0.0847, x: 0.039 },
      '300': { r: 0.0707, x: 0.038 },
      '350': { r: 0.0605, x: 0.038 },
      '400': { r: 0.0529, x: 0.037 },
      '500': { r: 0.0424, x: 0.036 },
      '600': { r: 0.0353, x: 0.036 }
    }
  };

  // Helper functions
  const convertToOhmsPerMeter = (value: number, unit: string): number => {
    const conversions: { [key: string]: number } = {
      'okm': value / 1000,
      'om': value,
      'mom': value / 1000,
      'okf': value / 304.8,
      'of': value / 0.3048,
      'mof': value / 304.8
    };
    return conversions[unit] || value;
  };

  const convertDistance = (value: number, unit: string): number => {
    const conversions: { [key: string]: number } = {
      'feet': value * 0.3048,
      'meters': value,
      'miles': value * 1609.34,
      'kilometers': value * 1000
    };
    return conversions[unit] || value;
  };

  const calculateResistance = () => {
    const distanceMeters = convertDistance(inputs.distance, inputs.distanceUnit);
    const conductors = inputs.conductors;

    let resistancePerMeter: number;
    let reactancePerMeter = 0;

    if (activeTab === 'nec') {
      const data = (necResistanceData as any)[inputs.necMaterial][inputs.necWireSize];
      resistancePerMeter = data.r / 304.8;
      reactancePerMeter = data.x / 304.8;

      if (inputs.necConduit === 'aluminum') {
        reactancePerMeter *= 0.95;
      } else if (inputs.necConduit === 'pvc') {
        reactancePerMeter *= 0.90;
      }
    } else if (activeTab === 'estimated') {
      const resistanceOhmPerKm = parseFloat(inputs.estWireSize);
      resistancePerMeter = resistanceOhmPerKm / 1000;
    } else {
      resistancePerMeter = convertToOhmsPerMeter(inputs.customResistance, inputs.customResistanceUnit);
    }

    const totalResistance = (resistancePerMeter * distanceMeters * 2) / conductors;
    const totalReactance = (reactancePerMeter * distanceMeters * 2) / conductors;

    return { resistance: totalResistance, reactance: totalReactance };
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const { resistance, reactance } = calculateResistance();
    let voltageDrop: number;

    if (inputs.phase === 'dc' || activeTab === 'estimated' || activeTab === 'custom') {
      voltageDrop = inputs.current * resistance;
    } else if (inputs.phase === 'ac') {
      const pf = inputs.necPf;
      voltageDrop = inputs.current * (resistance * pf + reactance * Math.sqrt(1 - pf * pf));
    } else {
      const pf = activeTab === 'nec' ? inputs.necPf : 0.85;
      voltageDrop = Math.sqrt(3) * inputs.current * (resistance * pf + reactance * Math.sqrt(1 - pf * pf));
    }

    const voltageDropPercent = (voltageDrop / inputs.voltage) * 100;
    const endVoltage = inputs.voltage - voltageDrop;

    setResults({
      voltageDrop,
      voltageDropPercent,
      endVoltage,
      totalResistance: resistance,
    });
  };

  // Responsive style helper
  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Colors
  const colors = {
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    secondary: '#7c3aed',
    accent: '#3b82f6',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#dc2626',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(37, 99, 235, 0.2)',
  };

  // Styles
  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, maxWidth: '800px', margin: '0 auto' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden' as const,
      margin: '0 auto',
    },
    { maxWidth: '100%' },
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const,
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700 },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const tabMenuStyle: React.CSSProperties = {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '2rem',
    borderBottom: '2px solid #e5e7eb',
    flexWrap: 'wrap',
  };

  const tabBtnStyle = (isActive: boolean) => getResponsiveStyle(
    {
      background: 'none',
      border: 'none',
      borderBottom: isActive ? '3px solid #2563eb' : '3px solid transparent',
      cursor: 'pointer',
      fontWeight: 600,
      color: isActive ? '#2563eb' : '#6b7280',
      transition: 'all 0.3s ease',
    },
    { padding: '0.75rem 1rem', fontSize: '0.9rem' },
    { padding: '0.75rem 1.25rem', fontSize: '0.95rem' },
    { padding: '0.75rem 1.5rem', fontSize: '1rem' }
  );

  const formGridStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1.5rem',
      marginBottom: '1.5rem',
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(2, 1fr)' }
  );

  const formGroupStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
  };

  const labelStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.925rem' },
    { fontSize: '0.95rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      border: '2px solid #e5e7eb',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      width: '100%',
      boxSizing: 'border-box' as const,
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.8rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' }
  );

  const selectStyle = { ...inputStyle };

  const inputWithUnitStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: '0.5rem',
  };

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem',
    },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1.05rem', fontSize: '1.05rem' },
    { padding: '1.1rem', fontSize: '1.1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderTop: '3px solid #10b981',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: '#065f46' },
    { fontSize: '1.35rem' },
    { fontSize: '1.425rem' },
    { fontSize: '1.5rem' }
  );

  const resultGridStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1rem',
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(2, 1fr)' }
  );

  const resultItemStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    },
    { padding: '1rem' },
    { padding: '1.125rem' },
    { padding: '1.25rem' }
  );

  const resultLabelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.9rem',
    color: '#6b7280',
    marginBottom: '0.5rem',
    fontWeight: 600,
  };

  const resultValueStyle = (type?: 'warning' | 'danger') => getResponsiveStyle(
    {
      display: 'block',
      fontWeight: 700,
      color: type === 'warning' ? colors.warning : type === 'danger' ? colors.danger : colors.success,
    },
    { fontSize: '1.35rem' },
    { fontSize: '1.425rem' },
    { fontSize: '1.5rem' }
  );

  const warningMessageStyle = getResponsiveStyle(
    {
      background: '#fef3c7',
      borderLeft: '4px solid #f59e0b',
      borderRadius: '0.5rem',
      color: '#92400e',
      marginTop: '1rem',
    },
    { padding: '0.875rem', fontSize: '0.9rem' },
    { padding: '0.95rem', fontSize: '0.95rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const errorMessageStyle = getResponsiveStyle(
    {
      background: '#fee2e2',
      borderLeft: '4px solid #dc2626',
      borderRadius: '0.5rem',
      color: '#991b1b',
      marginTop: '1rem',
    },
    { padding: '0.875rem', fontSize: '0.9rem' },
    { padding: '0.95rem', fontSize: '0.95rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(37, 99, 235, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem', marginTop: '1.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const strongStyle: React.CSSProperties = {
    color: colors.textPrimary,
    fontWeight: 600,
  };

  const linkStyle: React.CSSProperties = {
    color: colors.primary,
    textDecoration: 'none',
    borderBottom: '1px solid transparent',
    transition: 'border-color 0.3s ease',
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>⚡ {t("toolTitle")}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={tabMenuStyle}>
              <button
                type="button"
                style={tabBtnStyle(activeTab === 'nec')}
                onClick={() => setActiveTab('nec')}
              >
                {t("tabNec")}
              </button>
              <button
                type="button"
                style={tabBtnStyle(activeTab === 'estimated')}
                onClick={() => setActiveTab('estimated')}
              >
                {t("tabEstimated")}
              </button>
              <button
                type="button"
                style={tabBtnStyle(activeTab === 'custom')}
                onClick={() => setActiveTab('custom')}
              >
                {t("tabCustom")}
              </button>
            </div>

            {activeTab === 'nec' && (
              <div style={formGridStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("necMaterial")}</label>
                  <select
                    style={selectStyle}
                    value={inputs.necMaterial}
                    onChange={(e) => setInputs({...inputs, necMaterial: e.target.value})}
                  >
                    <option value="copper">{t("materialCopper")}</option>
                    <option value="aluminum">{t("materialAluminum")}</option>
                  </select>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("necWireSize")}</label>
                  <select
                    style={selectStyle}
                    value={inputs.necWireSize}
                    onChange={(e) => setInputs({...inputs, necWireSize: e.target.value})}
                  >
                    <option value="14">14 AWG</option>
                    <option value="12">12 AWG</option>
                    <option value="10">10 AWG</option>
                    <option value="8">8 AWG</option>
                    <option value="6">6 AWG</option>
                    <option value="4">4 AWG</option>
                    <option value="3">3 AWG</option>
                    <option value="2">2 AWG</option>
                    <option value="1">1 AWG</option>
                    <option value="1/0">1/0 AWG</option>
                    <option value="2/0">2/0 AWG</option>
                    <option value="3/0">3/0 AWG</option>
                    <option value="4/0">4/0 AWG</option>
                    <option value="250">250 kcmil</option>
                    <option value="300">300 kcmil</option>
                    <option value="350">350 kcmil</option>
                    <option value="400">400 kcmil</option>
                    <option value="500">500 kcmil</option>
                    <option value="600">600 kcmil</option>
                  </select>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("necConduit")}</label>
                  <select
                    style={selectStyle}
                    value={inputs.necConduit}
                    onChange={(e) => setInputs({...inputs, necConduit: e.target.value})}
                  >
                    <option value="pvc">{t("conduitPvc")}</option>
                    <option value="aluminum">{t("conduitAluminum")}</option>
                    <option value="steel">{t("conduitSteel")}</option>
                  </select>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("necPowerFactor")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    step="0.01"
                    min="0"
                    max="1"
                    value={inputs.necPf}
                    onChange={(e) => setInputs({...inputs, necPf: parseFloat(e.target.value)})}
                  />
                </div>
              </div>
            )}

            {activeTab === 'estimated' && (
              <div style={formGridStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("estMaterial")}</label>
                  <select
                    style={selectStyle}
                    value={inputs.estMaterial}
                    onChange={(e) => setInputs({...inputs, estMaterial: e.target.value})}
                  >
                    <option value="copper">{t("materialCopper")}</option>
                    <option value="aluminum">{t("materialAluminum")}</option>
                    <option value="silver">{t("materialSilver")}</option>
                    <option value="gold">{t("materialGold")}</option>
                  </select>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("estWireSize")}</label>
                  <select
                    style={selectStyle}
                    value={inputs.estWireSize}
                    onChange={(e) => setInputs({...inputs, estWireSize: e.target.value})}
                  >
                    <option value="0.05671">600 kcmil</option>
                    <option value="0.06805">500 kcmil</option>
                    <option value="0.08507">400 kcmil</option>
                    <option value="0.1134">300 kcmil</option>
                    <option value="0.1608">4/0 AWG</option>
                    <option value="0.2028">3/0 AWG</option>
                    <option value="0.2557">2/0 AWG</option>
                    <option value="0.3224">1/0 AWG</option>
                    <option value="0.4066">1 AWG</option>
                    <option value="0.5127">2 AWG</option>
                    <option value="0.6465">3 AWG</option>
                    <option value="0.8152">4 AWG</option>
                    <option value="1.296">6 AWG</option>
                    <option value="2.061">8 AWG</option>
                    <option value="3.277">10 AWG</option>
                    <option value="5.211">12 AWG</option>
                    <option value="8.286">14 AWG</option>
                    <option value="13.17">16 AWG</option>
                    <option value="20.95">18 AWG</option>
                    <option value="33.31">20 AWG</option>
                  </select>
                </div>
              </div>
            )}

            {activeTab === 'custom' && (
              <div style={formGridStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("customResistance")}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      type="number"
                      style={inputStyle}
                      step="0.001"
                      value={inputs.customResistance}
                      onChange={(e) => setInputs({...inputs, customResistance: parseFloat(e.target.value)})}
                    />
                    <select
                      style={{...selectStyle, minWidth: '120px'}}
                      value={inputs.customResistanceUnit}
                      onChange={(e) => setInputs({...inputs, customResistanceUnit: e.target.value})}
                    >
                      <option value="okm">Ω/km</option>
                      <option value="om">Ω/m</option>
                      <option value="mom">mΩ/m</option>
                      <option value="okf">Ω/1000ft</option>
                      <option value="of">Ω/ft</option>
                      <option value="mof">mΩ/ft</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            <div style={formGridStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t("voltage")}</label>
                <input
                  type="number"
                  style={inputStyle}
                  step="0.1"
                  value={inputs.voltage}
                  onChange={(e) => setInputs({...inputs, voltage: parseFloat(e.target.value)})}
                  required
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t("phase")}</label>
                <select
                  style={selectStyle}
                  value={inputs.phase}
                  onChange={(e) => setInputs({...inputs, phase: e.target.value})}
                >
                  <option value="dc">{t("phaseDc")}</option>
                  <option value="ac">{t("phaseAcSingle")}</option>
                  <option value="ac3">{t("phaseAc3")}</option>
                </select>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t("conductors")}</label>
                <select
                  style={selectStyle}
                  value={inputs.conductors}
                  onChange={(e) => setInputs({...inputs, conductors: parseInt(e.target.value)})}
                >
                  <option value="1">{t("conductors1")}</option>
                  <option value="2">{t("conductors2")}</option>
                  <option value="3">{t("conductors3")}</option>
                  <option value="4">{t("conductors4")}</option>
                </select>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t("distance")}</label>
                <div style={inputWithUnitStyle}>
                  <input
                    type="number"
                    style={inputStyle}
                    step="0.1"
                    value={inputs.distance}
                    onChange={(e) => setInputs({...inputs, distance: parseFloat(e.target.value)})}
                    required
                  />
                  <select
                    style={{...selectStyle, minWidth: '120px'}}
                    value={inputs.distanceUnit}
                    onChange={(e) => setInputs({...inputs, distanceUnit: e.target.value})}
                  >
                    <option value="feet">{t("unitFeet")}</option>
                    <option value="meters">{t("unitMeters")}</option>
                    <option value="miles">{t("unitMiles")}</option>
                    <option value="kilometers">{t("unitKilometers")}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t("current")}</label>
                <input
                  type="number"
                  style={inputStyle}
                  step="0.1"
                  value={inputs.current}
                  onChange={(e) => setInputs({...inputs, current: parseFloat(e.target.value)})}
                  required
                />
              </div>
            </div>

            <button type="submit" style={calculateBtnStyle}>
              {t("calculateButton")}
            </button>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>📊 {t("resultsTitle")}</h3>
              <div style={resultGridStyle}>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("resultVoltageDrop")}</span>
                  <span style={resultValueStyle()}>{results.voltageDrop.toFixed(3)} V</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("resultVoltageDropPercent")}</span>
                  <span style={resultValueStyle(
                    results.voltageDropPercent > 5 ? 'danger' : results.voltageDropPercent > 3 ? 'warning' : undefined
                  )}>
                    {results.voltageDropPercent.toFixed(2)}%
                  </span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("resultEndVoltage")}</span>
                  <span style={resultValueStyle()}>{results.endVoltage.toFixed(2)} V</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("resultTotalResistance")}</span>
                  <span style={resultValueStyle()}>{results.totalResistance.toFixed(6)} Ω</span>
                </div>
              </div>

              {results.voltageDropPercent > 5 && (
                <div style={errorMessageStyle}>
                  <strong>⚠️ {t("warningTitle")}</strong> {t("warningDanger")}
                </div>
              )}

              {results.voltageDropPercent > 3 && results.voltageDropPercent <= 5 && (
                <div style={warningMessageStyle}>
                  <strong>⚡ {t("noticeTitle")}</strong> {t("warningNotice")}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introPara1")}</p>
        <p style={paragraphStyle}>{t("introPara2")}</p>
        <p style={paragraphStyle}>{t("introPara3")}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t("step1Title")}</strong> {t("step1Desc")}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t("step2Title")}</strong> {t("step2Desc")}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t("step3Title")}</strong> {t("step3Desc")}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t("step4Title")}</strong> {t("step4Desc")}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t("step5Title")}</strong> {t("step5Desc")}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insightsPara1")}</p>

        <h3 style={h3Style}>{t("insightsWhyImportantTitle")}</h3>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>{t("insightsReason1Title")}</strong> {t("insightsReason1Desc")}</li>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>{t("insightsReason2Title")}</strong> {t("insightsReason2Desc")}</li>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>{t("insightsReason3Title")}</strong> {t("insightsReason3Desc")}</li>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>{t("insightsReason4Title")}</strong> {t("insightsReason4Desc")}</li>
        </ul>

        <h3 style={h3Style}>{t("insightsBestPracticesTitle")}</h3>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.5rem'}}>{t("insightsPractice1")}</li>
          <li style={{marginBottom: '0.5rem'}}>{t("insightsPractice2")}</li>
          <li style={{marginBottom: '0.5rem'}}>{t("insightsPractice3")}</li>
          <li style={{marginBottom: '0.5rem'}}>{t("insightsPractice4")}</li>
          <li style={{marginBottom: '0.5rem'}}>{t("insightsPractice5")}</li>
        </ul>

        <h3 style={h3Style}>{t("insightsStandardsTitle")}</h3>
        <p style={paragraphStyle}>{t("insightsStandardsPara")}</p>
      </section>

      {/* Details */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>

        <h3 style={h3Style}>{t("detailsCalculationTitle")}</h3>
        <p style={paragraphStyle}>{t("detailsCalculationPara1")}</p>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>{t("detailsDcTitle")}</strong> {t("detailsDcFormula")}</li>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>{t("detailsAcSingleTitle")}</strong> {t("detailsAcSingleFormula")}</li>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>{t("detailsAc3Title")}</strong> {t("detailsAc3Formula")}</li>
        </ul>

        <h3 style={h3Style}>{t("detailsFactorsTitle")}</h3>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>{t("detailsFactor1Title")}</strong> {t("detailsFactor1Desc")}</li>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>{t("detailsFactor2Title")}</strong> {t("detailsFactor2Desc")}</li>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>{t("detailsFactor3Title")}</strong> {t("detailsFactor3Desc")}</li>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>{t("detailsFactor4Title")}</strong> {t("detailsFactor4Desc")}</li>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>{t("detailsFactor5Title")}</strong> {t("detailsFactor5Desc")}</li>
        </ul>

        <h3 style={h3Style}>{t("detailsAcceptableTitle")}</h3>
        <p style={paragraphStyle}>{t("detailsAcceptablePara")}</p>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <h3 style={h3Style}>{t("faq1Question")}</h3>
        <p style={paragraphStyle}>{t("faq1Answer")}</p>

        <h3 style={h3Style}>{t("faq2Question")}</h3>
        <p style={paragraphStyle}>{t("faq2Answer")}</p>

        <h3 style={h3Style}>{t("faq3Question")}</h3>
        <p style={paragraphStyle}>{t("faq3Answer")}</p>

        <h3 style={h3Style}>{t("faq4Question")}</h3>
        <p style={paragraphStyle}>{t("faq4Answer")}</p>

        <h3 style={h3Style}>{t("faq5Question")}</h3>
        <p style={paragraphStyle}>{t("faq5Answer")}</p>

        <h3 style={h3Style}>{t("faq6Question")}</h3>
        <p style={paragraphStyle}>{t("faq6Answer")}</p>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ol style={listStyle}>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://www.interpower.com/ic/InfoPower/the-value-of-calculating-voltage-drop.html" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t("ref1")}
            </a>
          </li>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://www.keysight.com/used/us/en/knowledge/glossary/oscilloscopes/what-is-voltage-drop" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t("ref2")}
            </a>
          </li>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://electricityforum.com/voltage-drop" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t("ref3")}
            </a>
          </li>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://elek.com/articles/voltage-drop-calculation-method-with-examples/" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t("ref4")}
            </a>
          </li>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://www.flux.ai/p/blog/importance-of-voltage-drop-calculations" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t("ref5")}
            </a>
          </li>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://www.mikeholt.com/technnical-voltage-drop-calculations-part-one.php" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t("ref6")}
            </a>
          </li>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://en.wikipedia.org/wiki/Voltage_drop" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t("ref7")}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}